/**
 * PlayerCard — SofaScore-style card showing individual player stats + heatmap.
 *
 * Renders a compact card with:
 *   - Player header (jersey badge, name, position)
 *   - A `grid-cols-1 sm:grid-cols-2` grid holding, in this order: the
 *     two-column stat grid (all 17 metrics), the pass map (in-app only),
 *     the per-player control map (in-app only), the mini heatmap
 *
 * Client wave 2026-09-19 (EPIC-61, brief 6/AC12 — "compact ficha... stats,
 * pass map, control map and heat map in one view", PMA-COCK-214): every
 * section below the stat grid carried its own visible title (the mini
 * heatmap gained one this pass; Pass Map and Player Control Map already had
 * theirs), and `PassMapPitch.tsx`'s four stats moved beside its pitch
 * instead of a full-width bar beneath it. This is the COCKPIT ficha only
 * (mounted via `AnalyticsView.tsx` -> `PlayerAnalyticsView.tsx` ->
 * `PlayerCardPicker.tsx`, never the public share page — `hidePlayerHeatmaps`
 * is untouched below, still gates the pass map / control map exactly as
 * before). AC12's own wording said "public" but there is no public
 * per-player detail to compact — a drafting error the lead confirmed and
 * corrected in the spec.
 *
 * Client wave 2026-09-23 (EPIC-94, item 4/AC6 — dated reversal of the
 * stacked-sections layout above): "the player detail [becomes] a compact
 * ficha: stats top-left, pass map top-right..., then the player control map
 * and the heat map side by side underneath. Remove the full-width bottom
 * KPI row." All four sections below the header now live inside ONE
 * `grid-cols-1 sm:grid-cols-2` grid (see the grid's own comment below for
 * the exact placement math), replacing the previous vertical stack of
 * full-width `px-2 pb-2` blocks. `PassMapPitch.tsx`'s own big KPI tiles are
 * gone (that file's own doc comment). Every section testid is unchanged.
 */
import React, { useMemo } from "react";
import SoccerFieldHeatMap from "./SoccerFieldHeatMap";
import PassMapPitch from "./PassMapPitch";
import PlayerControlMap from "./PlayerControlMap";
import PlayerTouchMap from "./PlayerTouchMap";
import {
  computeHeatMapData,
  extractHeatPoints,
} from "../../utils/heatMapZones";
import { computePassMapData } from "../../utils/passMap";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { PlayerStats } from "../../hooks/usePlayerStats";
import PlayerAvatar from "../../../../components/PlayerAvatar";
import { formatDisplayName } from "../../../../lib/nameFormat";
import type { Team } from "../../types";

/* ------------------------------------------------------------------ */
/*  Stat definitions                                                   */
/* ------------------------------------------------------------------ */

interface StatDef {
  key: keyof PlayerStats;
  /** i18n key for the stat name (under logger namespace) */
  i18nKey: string;
  /** i18n key for the tooltip description */
  i18nInfoKey: string;
  /** Fallback English label */
  label: string;
  /** Fallback English tooltip */
  info: string;
  /** Short code used in data-testid only */
  abbr: string;
  group: "shooting" | "passing" | "general" | "discipline" | "defensive";
}

const STAT_DEFS: StatDef[] = [
  {
    key: "goals",
    i18nKey: "statGoals",
    i18nInfoKey: "statGoalsInfo",
    label: "Goals",
    info: "Total goals scored by the player",
    abbr: "G",
    group: "shooting",
  },
  {
    key: "assists",
    i18nKey: "statAssists",
    i18nInfoKey: "statAssistsInfo",
    label: "Assists",
    info: "Passes that directly led to a goal",
    abbr: "AST",
    group: "passing",
  },
  {
    key: "shotsOnTarget",
    i18nKey: "statShotsOnTarget",
    i18nInfoKey: "statShotsOnTargetInfo",
    label: "Shots on Target",
    info: "Shots that required a save or resulted in a goal",
    abbr: "SOT",
    group: "shooting",
  },
  {
    // PMA-COCK-165/167 (client feature request): outcome ∈ {OffTarget,
    // Post} — see usePlayerStats.ts (fe-events) for the exact field.
    key: "shotsOffTarget",
    i18nKey: "statShotsOffTarget",
    i18nInfoKey: "statShotsOffTargetInfo",
    label: "Shots Off Target",
    info: "Shot attempts that missed the goal",
    abbr: "SFT",
    group: "shooting",
  },
  {
    key: "shotsBlocked",
    i18nKey: "statShotsBlocked",
    i18nInfoKey: "statShotsBlockedInfo",
    label: "Shots Blocked",
    info: "This player's shot attempts blocked by a defender",
    abbr: "SBL",
    group: "shooting",
  },
  {
    // Re-added (PMA-COCK-167, client feature request): removed from this
    // grid by PMA-COCK-026's v1.18.5 addendum, kept computed in
    // usePlayerStats.ts throughout and still shown on PlayerStatsTable.
    // Now doubles as the credit for a blocked SHOT's picked blocker
    // (`data.blocked_by_player_id`, PMA-COCK-165) alongside the
    // pre-existing defensive `Block` event credit.
    key: "blocks",
    i18nKey: "statBlocks",
    i18nInfoKey: "statBlocksInfo",
    label: "Blocks",
    info: "Shots or passes blocked by the player",
    abbr: "BLK",
    group: "defensive",
  },
  {
    // PMA-COCK-177 (goalkeeper saves become a counted stat, user decision
    // 2026-09-09): the goalkeeper named in `data.saved_by_player_id` on a
    // Saved Shot/Header — see usePlayerStats.ts for the exact credit
    // (DirectShot's on-target-only tap, PMA-COCK-176, or the generic
    // More-actions Shot flow, PMA-COCK-172); never from a shootout kick.
    key: "saves",
    i18nKey: "statSaves",
    i18nInfoKey: "statSavesInfo",
    label: "Saves",
    info: "Saves credited to the goalkeeper on shots on target",
    abbr: "SV",
    group: "defensive",
  },
  {
    key: "penaltyGoals",
    i18nKey: "statPenaltyGoals",
    i18nInfoKey: "statPenaltyGoalsInfo",
    label: "Penalty Goals",
    info: "Goals scored from penalty kicks",
    abbr: "PGO",
    group: "shooting",
  },
  {
    // EPIC-107 (D1, client request 2026-10-06): duel rows restored, reversing
    // PMA-COCK-167's 2026-09-07 retirement. `DuelWon` is back on the wheel
    // (PMA-COCK-225); values come from `duelsWon`/`duelsLost`.
    key: "duelsWon",
    i18nKey: "statDuelsWon",
    i18nInfoKey: "statDuelsWonInfo",
    label: "Duels Won",
    info: "1v1 contests won (aerial and ground)",
    abbr: "DW",
    group: "general",
  },
  {
    key: "duelsLost",
    i18nKey: "statDuelsLost",
    i18nInfoKey: "statDuelsLostInfo",
    label: "Duels Lost",
    info: "1v1 contests lost (aerial and ground)",
    abbr: "DL",
    group: "general",
  },
  {
    // PMA-COCK-167 (client feature request): "Toques" — every event this
    // player authored, excluding cards, substitutions, fouls committed,
    // VAR, injury, period/clock control events, and shootout kicks. See
    // usePlayerStats.ts (fe-events) for the authoritative exclusion list.
    key: "touches",
    i18nKey: "statTouches",
    i18nInfoKey: "statTouchesInfo",
    label: "Touches",
    info: "Events this player authored, excluding cards, substitutions, fouls committed, VAR, injury, and clock-control events",
    abbr: "TCH",
    group: "general",
  },
  {
    key: "passesGood",
    i18nKey: "statPassesGood",
    i18nInfoKey: "statPassesGoodInfo",
    label: "Good Passes",
    info: "Completed passes that reached a teammate",
    abbr: "PG",
    group: "passing",
  },
  {
    key: "passesBad",
    i18nKey: "statPassesBad",
    i18nInfoKey: "statPassesBadInfo",
    label: "Bad Passes",
    info: "Incomplete passes intercepted or out of play",
    abbr: "PB",
    group: "passing",
  },
  {
    key: "passesReceived",
    i18nKey: "statPassesReceived",
    i18nInfoKey: "statPassesReceivedInfo",
    label: "Passes Received",
    info: "Successful receptions from a teammate's pass",
    abbr: "PR",
    group: "passing",
  },
  {
    key: "foulsCommitted",
    i18nKey: "statFoulsCommitted",
    i18nInfoKey: "statFoulsCommittedInfo",
    label: "Fouls Done",
    info: "Fouls committed by the player",
    abbr: "FC",
    group: "discipline",
  },
  {
    key: "foulsReceived",
    i18nKey: "statFoulsReceived",
    i18nInfoKey: "statFoulsReceivedInfo",
    label: "Fouls Received",
    info: "Fouls suffered by the player",
    abbr: "FR",
    group: "discipline",
  },
  {
    // EPIC-98 (item 4/AC4): a fouled player winning a penalty — the
    // PMA-COCK-038 foul-award SetPiece/Penalty `Complete` event, credited
    // to the fouled player, not the taker. See usePlayerStats.ts for the
    // exact count. Placed beside "Fouls Received" (same discipline group):
    // this is that same kind of drawn foul, just the rarer in-the-box case.
    key: "penaltiesWon",
    i18nKey: "statPenaltiesWon",
    i18nInfoKey: "statPenaltiesWonInfo",
    label: "Penalties Won",
    info: "Penalties won by the player being fouled",
    abbr: "PW",
    group: "discipline",
  },
  {
    key: "yellowCards",
    i18nKey: "statYellowCards",
    i18nInfoKey: "statYellowCardsInfo",
    label: "Yellow Cards",
    info: "Yellow cards received",
    abbr: "YC",
    group: "discipline",
  },
  {
    key: "redCards",
    i18nKey: "statRedCards",
    i18nInfoKey: "statRedCardsInfo",
    label: "Red Cards",
    info: "Red cards received (direct or double yellow)",
    abbr: "RC",
    group: "discipline",
  },
];

const GROUP_COLORS: Record<StatDef["group"], string> = {
  shooting: "text-amber-400",
  passing: "text-emerald-400",
  general: "text-slate-200",
  discipline: "text-rose-400",
  defensive: "text-cyan-400",
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface PlayerCardProps {
  stats: PlayerStats;
  events: MatchEvent[];
  /** The player's own team — needed only for `PlayerControlMap`'s crest +
   * color (PMA-COCK-168), same object `AnalyticsView.tsx`/
   * `PlayerCardPicker.tsx` already hold as `match.home_team`/`away_team`. */
  team: Team;
  /** When true, hide this player's in-app-only pass map + control map.
   * Mirrors the `hidePlayerHeatmaps` flag already honored by
   * `HeatMapSection`/`MatchAnalytics` — `PlayerCard` itself is only ever
   * rendered by the cockpit's `AnalyticsView.tsx` (never
   * `PublicMatchAnalytics.tsx`), so this is defense-in-depth for any future
   * reuse on a public surface. */
  hidePlayerHeatmaps?: boolean;
  /** EPIC-107 D1: minutes on the pitch from `computeMinutesPlayed`; the
   * header shows "N' jugados" only when provided. */
  minutesPlayed?: number;
  t: (key: string, fallback?: string) => string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PlayerCard: React.FC<PlayerCardProps> = ({
  stats,
  events,
  team,
  hidePlayerHeatmaps = false,
  minutesPlayed,
  t,
}) => {
  const heatData = useMemo(
    () => computeHeatMapData(events, undefined, stats.playerId),
    [events, stats.playerId],
  );
  const heatPoints = useMemo(
    () => extractHeatPoints(events, undefined, stats.playerId),
    [events, stats.playerId],
  );
  const passMapData = useMemo(
    () => computePassMapData(events, stats.playerId),
    [events, stats.playerId],
  );

  const isHome = stats.teamSide === "home";
  const accentColor = isHome ? "#10b981" : "#6366f1";
  const badgeBg = isHome ? "bg-emerald-500" : "bg-blue-500";

  return (
    <div
      className="bg-slate-800/70 rounded-xl border border-slate-700 overflow-visible flex flex-col"
      data-testid={`player-card-${stats.playerId}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-slate-700/60 bg-slate-900/40">
        <PlayerAvatar
          player={{
            full_name: formatDisplayName(stats.playerName),
            photo_url: stats.photoUrl,
            photo_image: stats.photoImage,
          }}
          size={32}
        />
        <span
          className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white ${badgeBg}`}
        >
          {stats.jerseyNumber}
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-semibold text-slate-100 truncate"
            title={formatDisplayName(stats.playerName)}
          >
            {formatDisplayName(stats.playerName)}
          </div>
          <div
            className="text-xs text-slate-200 uppercase tracking-wide"
            data-testid={`player-card-header-line-${stats.playerId}`}
          >
            {t("analytics.totalEventsLabel", "Events")}: {stats.totalEvents}
            {minutesPlayed !== undefined && (
              <span
                data-testid={`player-card-minutes-${stats.playerId}`}
                className="ml-2 normal-case"
              >
                {t("analytics.minutesPlayed", "{{count}}' played")
                  .split("{{count}}")
                  .join(String(minutesPlayed))}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* EPIC-94, item 4/AC6 (client wave 2026-09-23) — dated reversal of
          the PMA-COCK-214 stacked-sections layout immediately below: "stats
          top-left, pass map top-right... player control map and the heat
          map side by side underneath. Remove the full-width bottom KPI
          row." One `grid-cols-1 sm:grid-cols-2` grid holds all four
          sections in DOM order (stats, pass map, control map, heat map) so
          the default grid-auto-flow places them exactly as requested: at
          `sm:` and up, stats=top-left, pass map=top-right, control map=
          bottom-left, heat map=bottom-right; below `sm:` every section
          stacks to one column, same order. The "big KPI tiles" the client
          means are `PassMapPitch.tsx`'s own four stat tiles — removed in
          that file, see its own doc comment; nothing to remove here beyond
          the stats grid's own wrapper. */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-3 pb-2"
        data-testid={`player-card-grid-${stats.playerId}`}
      >
        {/* Stats grid (two columns) — top-left */}
        <div data-testid={`player-card-stats-${stats.playerId}`}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {STAT_DEFS.map((def) => {
              const val = stats[def.key] as number;
              const translatedName = t(def.i18nKey, def.label);
              const translatedInfo = t(def.i18nInfoKey, def.info);
              return (
                <div
                  key={def.key}
                  className="group/stat relative flex items-center justify-between py-0.5 border-b border-slate-700/30 last:border-b-0"
                  data-testid={`pc-${stats.playerId}-${def.abbr}`}
                >
                  <span
                    className="text-xs text-slate-200 truncate cursor-help"
                    data-testid={`pc-${stats.playerId}-${def.abbr}-label`}
                  >
                    {translatedName}
                  </span>
                  {/* Instant tooltip */}
                  <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 hidden group-hover/stat:block">
                    <div className="whitespace-nowrap rounded bg-slate-950 border border-slate-600 px-2 py-1 text-[10px] text-slate-200 shadow-lg">
                      {translatedInfo}
                    </div>
                  </div>
                  <span
                    data-testid={`pc-${stats.playerId}-${def.abbr}-value`}
                    className={`text-sm font-semibold tabular-nums ${
                      val === 0 ? "text-slate-400" : GROUP_COLORS[def.group]
                    }`}
                  >
                    {val === 0 ? "–" : val}
                  </span>
                </div>
              );
            })}
            {/* PMA-COCK-164/167: shootout kicks are their own stat, never
                folded into `penalties`/`goals` — see usePlayerStats.ts.
                Shown only when the player actually took a shootout kick, so
                a regular-time-only match never shows a bare "0/0" row. */}
            {stats.shootoutKicks > 0 && (
              <div
                className="group/stat relative col-span-2 flex items-center justify-between py-0.5 border-b border-slate-700/30 last:border-b-0"
                data-testid={`pc-${stats.playerId}-PENT`}
              >
                <span
                  className="text-xs text-slate-200 truncate cursor-help"
                  data-testid={`pc-${stats.playerId}-PENT-label`}
                >
                  {t("statShootoutPenalties", "Shootout Penalties")}
                </span>
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 hidden group-hover/stat:block">
                  <div className="whitespace-nowrap rounded bg-slate-950 border border-slate-600 px-2 py-1 text-[10px] text-slate-200 shadow-lg">
                    {t(
                      "statShootoutPenaltiesInfo",
                      "Penalty shootout kicks scored / taken",
                    )}
                  </div>
                </div>
                <span
                  data-testid={`pc-${stats.playerId}-PENT-value`}
                  className="text-sm font-semibold tabular-nums text-amber-400"
                >
                  {stats.shootoutGoals}/{stats.shootoutKicks}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Pass map — top-right. In-app cockpit analytics only (never on the
            public share page; see `hidePlayerHeatmaps` doc above). Its own
            four KPIs now sit beside its title (see PassMapPitch.tsx). */}
        {!hidePlayerHeatmaps && (
          <div data-testid={`passmap-section-${stats.playerId}`}>
            <PassMapPitch
              data={passMapData}
              title={t("analytics.passMap", "Pass Map")}
              t={t}
              data-testid={`passmap-player-${stats.playerId}`}
            />
          </div>
        )}

        {/* Per-player control map (PMA-COCK-168) — bottom-left, same
            in-app-only gating as the pass map above. Testid is the bare
            `player-control-map`, not suffixed by playerId: exactly one
            `PlayerCard` renders at a time now (via `PlayerCardPicker`), so
            there is never more than one on screen to disambiguate —
            matches the fixed testid list in the contract entry. */}
        {!hidePlayerHeatmaps && (
          <div>
            <PlayerControlMap
              team={team}
              side={stats.teamSide}
              playerId={stats.playerId}
              events={events}
              t={t}
            />
          </div>
        )}

        {/* Mini heatmap — bottom-right. Carries its own `title` badge
            (`SoccerFieldHeatMap.tsx`'s existing prop, PMA-COCK-214),
            matching Pass Map's and Player Control Map's own labelled
            sections. */}
        <div>
          <SoccerFieldHeatMap
            data={heatData}
            points={heatPoints}
            title={t("analytics.heatMap", "Heat Map")}
            accentColor={accentColor}
            side={stats.teamSide}
            t={t}
            data-testid={`heatmap-player-${stats.playerId}`}
          />
        </div>

        {/* Touch map (EPIC-107 D1) — fifth panel. Always shown (it is
            per-player data the share page never mounts this card with). */}
        <div>
          <PlayerTouchMap
            events={events}
            playerId={stats.playerId}
            t={t}
            data-testid={`player-touch-map-${stats.playerId}`}
          />
        </div>
      </div>
    </div>
  );
};

export default PlayerCard;
